import { DateTime } from 'luxon';
import { FC, useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import { Controls, ControlsButton } from '@/components/Controls';
import DateRangeFilter from '@/components/DateRangeFilter';
import { Box, Title } from '@/components/Dropdown';
import Text from '@/components/Text';
import useCompaniesOptions from '@/hooks/useCompaniesOptions';
import useDepartmentsOptions from '@/hooks/useDepartmentsOptions';
import IdFilterDropdown from '@/pages/InvoiceList/components/IdFilterDropdown';
import { StyledText } from '@/pages/InvoiceList/components/InvoicesFilterDropdown';

const StyledBox = styled(Box)`
  width: 100%;
  padding: 26px 24px 32px;
`;

const FilterHeading = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
`;

const FilterFooter = styled.div`
  margin-top: 24px;
  text-align: right;
`;

const BodyHeaderLine = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.colors.grayscaleLight};
  flex: 1;
  margin: 0 16px;
`;

const ResetButton = styled.button`
  background: ${({ theme }) => theme.colors.errorLight};
  height: 23px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  border-radius: 4px;
  border: 1px solid transparent;
  transition: all ease-out 0.2s;
  &:hover,
  &:active {
    border-color: ${({ theme }) => theme.colors.error};
  }
  &:disabled {
    background: ${({ theme }) => theme.colors.grayscaleLight};
    cursor: not-allowed;
    &:hover,
    &:active {
      border-color: transparent;
    }
  }
`;

const FilterGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 20px;
`;

const DateFilterTitle = styled(Title)`
  margin-bottom: 12px;
  padding: 0;
`;

const DatePresets = styled(Controls)`
  display: inline-flex;
  margin-bottom: 12px;
`;

const StyledButton = styled(Button)`
  font-size: 14px;
  min-width: 120px;
  height: 38px;
  padding: 0 20px;
`;

const ApplyButton = styled(StyledButton)`
  margin-left: 16px;
`;

const datePresets = [
  {
    title: 'This month',
    getRange: () => {
      const now = DateTime.now();
      return { rangeStart: now.startOf('month'), rangeEnd: now.endOf('month') };
    },
  },
  {
    title: 'Last month',
    getRange: () => {
      const lastMonth = DateTime.now().minus({ months: 1 });
      return {
        rangeStart: lastMonth.startOf('month'),
        rangeEnd: lastMonth.endOf('month'),
      };
    },
  },
];

interface Props {
  closeFilter: () => void;
  selectedDepartments: number[];
  setSelectedDepartments: React.Dispatch<React.SetStateAction<number[]>>;
  selectedCompanies: number[];
  setSelectedCompanies: React.Dispatch<React.SetStateAction<number[]>>;
  selectedDateRange: { rangeStart?: DateTime; rangeEnd?: DateTime };
  setSelectedDateRange: React.Dispatch<
    React.SetStateAction<{ rangeStart?: DateTime; rangeEnd?: DateTime }>
  >;
}

const VacationsApprovalFilterBox: FC<Props> = ({
  closeFilter,
  selectedDepartments,
  setSelectedDepartments,
  selectedCompanies,
  setSelectedCompanies,
  selectedDateRange,
  setSelectedDateRange,
}) => {
  const [selectedLocalDepartments, setSelectedLocalDepartments] =
    useState<number[]>(selectedDepartments);

  const [selectedLocalCompanies, setSelectedLocalCompanies] =
    useState<number[]>(selectedCompanies);

  const [selectedLocalDateRange, setSelectedLocalDateRange] = useState<{
    rangeStart?: DateTime;
    rangeEnd?: DateTime;
  }>(selectedDateRange);

  const { data: departmentOptions = [] } = useDepartmentsOptions();
  const { data: companyOptions = [] } = useCompaniesOptions();

  const visibleDepartmentOptions = departmentOptions.filter(
    (dept) => !dept.hide_from_dropdowns,
  );

  const visibleCompanyOptions = companyOptions.filter(
    (p) => !p.hide_from_dropdowns,
  );

  const isPresetActive = (
    getRange: () => {
      rangeStart: DateTime;
      rangeEnd: DateTime;
    },
  ) => {
    const { rangeStart, rangeEnd } = getRange();
    return (
      selectedLocalDateRange.rangeStart?.toISODate() ===
        rangeStart.toISODate() &&
      selectedLocalDateRange.rangeEnd?.toISODate() === rangeEnd.toISODate()
    );
  };

  const onPresetClick = (
    getRange: () => { rangeStart: DateTime; rangeEnd: DateTime },
  ) => {
    setSelectedLocalDateRange(isPresetActive(getRange) ? {} : getRange());
  };

  const onFilterApply = () => {
    setSelectedDepartments(selectedLocalDepartments);
    setSelectedCompanies(selectedLocalCompanies);
    setSelectedDateRange(selectedLocalDateRange);
    closeFilter();
  };

  const onFilterReset = () => {
    setSelectedDepartments([]);
    setSelectedLocalDepartments([]);
    setSelectedCompanies([]);
    setSelectedLocalCompanies([]);
    setSelectedDateRange({});
    setSelectedLocalDateRange({});
    closeFilter();
  };

  return (
    <StyledBox>
      <FilterHeading>
        <Text $type="button">Filter</Text>
        <BodyHeaderLine />
        <ResetButton onClick={() => onFilterReset()}>
          <Text $color="error" $type="smallHeadline">
            Reset
          </Text>
        </ResetButton>
      </FilterHeading>
      <FilterGroup>
        <div>
          <IdFilterDropdown
            filter={selectedLocalDepartments}
            setFilter={setSelectedLocalDepartments}
            options={visibleDepartmentOptions.map((dept) => ({
              name: dept.label,
              value: dept.value,
            }))}
            title="By Department:"
          />
        </div>
        <div>
          <IdFilterDropdown
            filter={selectedLocalCompanies}
            setFilter={setSelectedLocalCompanies}
            options={visibleCompanyOptions.map((company) => ({
              name: company.label,
              value: company.value,
            }))}
            title="By Company:"
          />
        </div>
        <div>
          <DateFilterTitle $color="black" $type="smallBold">
            By Vacation Date:
            <StyledText
              as="button"
              $color="gray"
              $type="extraSmall"
              disabled={
                !selectedLocalDateRange.rangeStart &&
                !selectedLocalDateRange.rangeEnd
              }
              onClick={() => setSelectedLocalDateRange({})}
            >
              Clear
            </StyledText>
          </DateFilterTitle>
          <DatePresets>
            {datePresets.map((preset) => (
              <ControlsButton
                key={preset.title}
                active={isPresetActive(preset.getRange)}
                onClick={() => onPresetClick(preset.getRange)}
              >
                {preset.title}
              </ControlsButton>
            ))}
          </DatePresets>
          <DateRangeFilter
            selectedDateRange={selectedLocalDateRange}
            setSelectedDateRange={setSelectedLocalDateRange}
            variant="select"
            openDownwardOnly
          />
        </div>
      </FilterGroup>
      <FilterFooter>
        <StyledButton onClick={closeFilter} color="secondary">
          Cancel
        </StyledButton>
        <ApplyButton color="colorized" onClick={onFilterApply}>
          Apply filter
        </ApplyButton>
      </FilterFooter>
    </StyledBox>
  );
};

export default VacationsApprovalFilterBox;
